import { isAutomaticExpectedBehaviorMetric } from "@/metrics/registry"

import { asJsonObjectOrNull, type JsonObject } from "@/types/json"

import type { SimulationLaunchMetricRecordsByName } from "./types"

const AUTOMATIC_CONVERSATION_METRIC_IDS = new Set([
  "7VHk6dTjvBcuV6XYPmaeGq",
  "mymKvEg6ZA65srXbTX5wSM",
  "29BlkepvvX19ebbLDB0y6Q",
])

export const UNRESOLVED_METRIC_SELECTION_MESSAGE =
  "Some selected metric definitions could not be resolved. Retry loading the configuration or remove those selections before continuing."

export function getRunMetricSelection(config: JsonObject | null | undefined): (
  | { metricIds: string[] }
  | { legacyKeys: string[] }
) & {
  hasAutomaticMetrics: boolean
} {
  const automaticIds = new Set(
    Array.isArray(config?.automatic_metric_ids)
      ? config.automatic_metric_ids.filter(
          (id): id is string =>
            typeof id === "string" && AUTOMATIC_CONVERSATION_METRIC_IDS.has(id),
        )
      : [],
  )
  const metricIds = config?.metric_ids
  // Empty legacy ID lists still defer to the populated metric map.
  if (Array.isArray(metricIds) && metricIds.length > 0) {
    return {
      hasAutomaticMetrics: automaticIds.size > 0,
      metricIds: metricIds.filter(
        (id): id is string => typeof id === "string" && !automaticIds.has(id),
      ),
    }
  }
  const metrics =
    asJsonObjectOrNull(config?.metrics_config) ??
    asJsonObjectOrNull(config?.metrics)
  return {
    hasAutomaticMetrics: automaticIds.size > 0,
    legacyKeys: Object.keys(metrics ?? {}).filter(
      (id) => !automaticIds.has(id),
    ),
  }
}

export function getConfiguredMetricNames(
  enabledMetrics: string[],
  metricRecords: SimulationLaunchMetricRecordsByName,
): string[] {
  return enabledMetrics.filter(
    (name) => !isAutomaticExpectedBehaviorMetric(metricRecords[name]),
  )
}

// ID fields establish identity; legacy map keys may instead be customer names.
export function getPrefilledMetricNames(
  selection: { metricIds: string[] } | { legacyKeys: string[] },
  metricIdToName: Record<string, string>,
  metricRecords: SimulationLaunchMetricRecordsByName,
): string[] {
  const names =
    "metricIds" in selection
      ? selection.metricIds
          .filter((id) => !isAutomaticExpectedBehaviorMetric({ id }))
          .map((id) => metricIdToName[id] || id)
      : selection.legacyKeys.map((key) =>
          metricRecords[key] ? key : metricIdToName[key] || key,
        )
  return [...new Set(getConfiguredMetricNames(names, metricRecords))]
}

export function getSelectedMetricIds(
  enabledMetrics: string[],
  metricRecords: SimulationLaunchMetricRecordsByName,
): string[] | null {
  const metricIds: string[] = []
  for (const metricName of getConfiguredMetricNames(
    enabledMetrics,
    metricRecords,
  )) {
    const metricId = metricRecords[metricName]?.id
    if (!metricId) {
      return null
    }
    metricIds.push(metricId)
  }
  return metricIds
}
